import React from 'react';
import {
    BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList
} from 'recharts';
import { StateData } from '../services/regionsAPI';

interface StatesBarChartProps {
    data: StateData[];
    selectedState: string | null;
    onStateClick: (estado: string) => void;
    isSupervisor?: boolean;
}

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short',
    }).format(value);

const BarTick = (props: any) => {
    const { x, y, payload } = props;
    const raw = String(payload.value ?? '');
    const maxLen = 18;
    const display = raw.length > maxLen ? raw.substring(0, maxLen - 2) + '…' : raw;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={-8}
                y={0}
                dy="0.35em"
                textAnchor="end"
                fontSize={11}
                fontWeight={700}
                fill="var(--text-primary, #111827)"
                style={{ textTransform: 'uppercase', fontFamily: FONT }}
            >
                {display}
            </text>
        </g>
    );
};

const BarTickVertical = (props: any) => {
    const { x, y, payload } = props;
    const raw = String(payload.value ?? '');
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={12}
                textAnchor="middle"
                fontSize={12}
                fontWeight={700}
                fill="var(--text-primary, #111827)"
                style={{ textTransform: 'uppercase', fontFamily: FONT }}
            >
                {raw}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const rowData = payload[0].payload;
    const isOutros = rowData.ESTADO === 'OUTROS';

    return (
        <div style={{
            background: 'var(--bg-widget)',
            borderRadius: 'var(--radius-base, 8px)',
            padding: '12px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid var(--border-primary)',
            fontSize: 12,
            minWidth: 200,
            fontFamily: FONT,
        }}>
            <p style={{
                fontWeight: 700,
                color: 'var(--text-primary, #111827)',
                margin: '0 0 8px',
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                borderBottom: '1px solid var(--border-primary)',
                paddingBottom: 6,
            }}>
                {rowData.ESTADO}
            </p>
            {isOutros && rowData.othersList && (() => {
                const maxOthersVenda = Math.max(...rowData.othersList.map((s: any) => s.totalVenda));
                return (
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 600, fontSize: 11, marginBottom: 8, textTransform: 'uppercase' }}>Estados Agrupados</div>
                        <div
                            style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 4 }}
                        >
                            {rowData.othersList.map((s: any) => {
                                const percent = s.totalVenda > 0 && maxOthersVenda > 0 ? (s.totalVenda / maxOthersVenda) * 100 : 0;
                                return (
                                    <div key={s.ESTADO} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 22, fontSize: 11, fontWeight: 800, color: '#475569' }}>{s.ESTADO}</span>
                                        <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                            {s.totalVenda > 0 && (
                                                <div style={{ width: `${percent}%`, height: '100%', background: 'var(--theme-chart-start, #f59e0b)', borderRadius: 3 }} />
                                            )}
                                        </div>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', minWidth: 65, textAlign: 'right' }}>
                                            {formatCurrency(s.totalVenda)}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0' }}>
                <span style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 500 }}>Vendas</span>
                <span style={{ fontWeight: 700, color: 'var(--text-primary, #111827)' }}>
                    {formatCurrency(payload[0]?.value ?? 0)}
                </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '3px 0' }}>
                <span style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 500 }}>Participação</span>
                <span style={{ fontWeight: 700, color: '#0ea5e9' }}>
                    {rowData.participacao?.toFixed(1)}%
                </span>
            </div>
        </div>
    );
};

const StatesBarChart: React.FC<StatesBarChartProps> = ({ data, selectedState, onStateClick, isSupervisor = true }) => {
    if (!data?.length) {
        return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8',
            fontWeight: 600, fontSize: 13, fontFamily: FONT }}>Nenhum estado encontrado.</div>;
    }

    const isVerticalBars = !isSupervisor;

    const sorted = [...data].sort((a, b) => b.totalVenda - a.totalVenda);
    const top10 = sorted.slice(0, 10);
    const others = sorted.slice(10);

    const chartData = others.length > 0
        ? [
            ...top10,
            {
                ESTADO: 'OUTROS',
                totalVenda: others.reduce((sum, s) => sum + (Number(s.totalVenda) || 0), 0),
                totalMargem: others.reduce((sum, s) => sum + (Number(s.totalMargem) || 0), 0),
                totalPares: others.reduce((sum, s) => sum + (Number(s.totalPares) || 0), 0),
                participacao: others.reduce((sum, s) => sum + (Number(s.participacao) || 0), 0),
                othersList: others,
            } as any
        ]
        : top10;

    const isOutros = (entry: any) => entry?.ESTADO === 'OUTROS';

    const handleBarClick = (entry: any) => {
        if (!isOutros(entry)) {
            onStateClick(entry.ESTADO);
        }
    };

    const hasSelection = !!selectedState;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={chartData}
                layout={isVerticalBars ? "horizontal" : "vertical"}
                margin={isVerticalBars ? { top: 30, right: 0, left: 0, bottom: 0 } : { top: 4, right: 130, left: 0, bottom: 4 }}
                barCategoryGap={isVerticalBars ? "35%" : "25%"}
            >
                <CartesianGrid strokeDasharray="3 3" horizontal={isVerticalBars} vertical={!isVerticalBars} stroke="#f0f0f0" />
                
                {isVerticalBars ? (
                    <>
                        <XAxis
                            dataKey="ESTADO"
                            type="category"
                            axisLine={false}
                            tickLine={false}
                            tick={<BarTickVertical />}
                            height={30}
                        />
                        <YAxis type="number" hide domain={[0, (max: number) => max * 1.2]} />
                    </>
                ) : (
                    <>
                        <XAxis type="number" hide domain={[0, (max: number) => max * 1.35]} />
                        <YAxis
                            dataKey="ESTADO"
                            type="category"
                            axisLine={false}
                            tickLine={false}
                            width={75}
                            tick={<BarTick />}
                        />
                    </>
                )}

                <Tooltip 
                    content={<CustomTooltip />} 
                    cursor={{ fill: 'rgba(0,0,0,0.03)' }} 
                />
                <Bar
                    dataKey="totalVenda"
                    radius={isVerticalBars ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    barSize={isVerticalBars ? 48 : 16}
                    onClick={(data) => handleBarClick(data.payload)}
                >
                    {chartData.map((entry, index) => {
                        const isSelected = selectedState === entry.ESTADO;
                        const dimmed = hasSelection && !isSelected;
                        
                        let fill = isOutros(entry) ? '#9ca3af' : 'var(--theme-chart-start, #dc2626)';
                        if (isSelected) fill = 'var(--theme-primary, #b91c1c)';

                        return (
                            <Cell
                                key={`cell-${index}`}
                                fill={fill}
                                opacity={dimmed && !isOutros(entry) ? 0.25 : 1}
                                cursor={isOutros(entry) ? 'default' : 'pointer'}
                            />
                        );
                    })}
                    <LabelList
                        dataKey="totalVenda"
                        position={isVerticalBars ? "top" : "right"}
                        content={({ x, y, width, height: h, value }: any) => {
                            if (isVerticalBars) {
                                return (
                                    <text
                                        x={(x as number) + (width as number) / 2}
                                        y={(y as number) - 8}
                                        textAnchor="middle"
                                        dominantBaseline="bottom"
                                        fontSize={13}
                                        fontWeight={700}
                                        fill="var(--text-primary, #111827)"
                                        style={{ pointerEvents: 'none', fontFamily: FONT }}
                                    >
                                        {formatCurrency(value)}
                                    </text>
                                );
                            }
                            return (
                                <text
                                    x={(x as number) + (width as number) + 8}
                                    y={(y as number) + (h as number) / 2}
                                    textAnchor="start"
                                    dominantBaseline="central"
                                    fontSize={13}
                                    fontWeight={700}
                                    fill="var(--text-primary, #111827)"
                                    style={{ pointerEvents: 'none', fontFamily: FONT }}
                                >
                                    {formatCurrency(value)}
                                </text>
                            );
                        }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default StatesBarChart;

